import { z } from "zod";
import { coachBriefLine, isUnconfirmedHypothesis } from "./coach-brief";
import type { CoachMemory, CoachMemoryItem } from "./coach-memory";
import type { CoachNow } from "./coach-now";
import { F01_MAIN_HELP_LEADS } from "./f01-memory";
import { OPEN_HANDOFF_STATUSES, type HandoffStatus } from "./handoff";

// TES-1101 (Ö-320–323, Emelie 2026-10-04) — the coach's fast top summary.
//
// A-01, "SLUTLIGT ÖVERORDNAT MVP-BESLUT — PATIENTÖVERSIKT …":
// "Direkt under patientens namn visas en kompakt, alltid synlig översikt. Den innehåller en kort coachrelevant patientsammanfattning, behandlingsmånad och aktuell fas, nästa viktiga steg, neutral status för eventuell medicinsk uppföljning samt öppen överenskommelse eller planerad uppföljning när sådan finns."
//
// What this shape adds is only what the persistent header (coach-patient.ts)
// does not already carry. Identity, weight, height, BMI, goal weight, phase,
// treatment month, plan and coach access stay in the header and are not
// repeated here. This adds:
//
//   - mainHelp: what she mainly wants help with. F-01's "Viktigast just nu"
//     rows (F01_MAIN_HELP_LEADS). F-02A's `med_main_question` is NOT read: it
//     is a medical intake answer addressed to the doctor, and the coach does
//     not see medical original answers (A-01's medical boundary).
//   - goalsAndNeeds: her other goals, the support she wants and the relevant
//     considerations, a few lines with a count of the rest;
//   - nextStep: the one next coach task or follow-up;
//   - handoffs: open handoffs to the coach, and her own still waiting on the
//     doctor, as counts and dates only, plus the step the coach takes next
//     on the ones addressed to her (TES-1402, Ö-550);
//   - medicalStatus: the neutral sentence from "Just nu", when a case is open.
//   - registeredSymptoms: TES-1448 (Ö-709), whether she has a symptom in
//     progress, as a yes/no for her active assigned coach and nothing else.
//
// Deterministic, no AI. Every line is a Patientminnet row quoted as stored
// with its source and date (coachBriefLine), and the same unconfirmed AI
// observations "Patienten i korthet" leaves out are left out here. Nothing is
// written: it is composed on read from CoachNow, the memory and the handoff
// rows, so it cannot drift from the surfaces that act on them.

export const coachSummaryLineSchema = z.object({
  itemId: z.string(),
  body: z.string(),
  sourceLabel: z.string(),
  observedAt: z.string(),
  fromF01: z.boolean(),
});

export const COACH_NEXT_STEP_KINDS = [
  // coach_access=INACTIVE: no new coach contact, so no coach task.
  "no_coach_access",
  // TES-1121 (Ö-342 a) — coach_access=INACTIVE because she has never had a
  // subscription: the coach chat opens when one starts.
  "no_coach_before_subscription",
  "message",
  "checkin",
  "follow_up_due",
  "handoff",
  "follow_up_scheduled",
  "checkin_window_open",
  "next_checkin",
  // TES-1118 — nothing waiting, and no weekly check-in because she has no
  // active subscription.
  "no_checkin_without_subscription",
] as const;
export const coachNextStepKindSchema = z.enum(COACH_NEXT_STEP_KINDS);
export type CoachNextStepKind = z.infer<typeof coachNextStepKindSchema>;

const handoffCountSchema = z.object({
  open: z.number().int().nonnegative(),
  latestAt: z.string().nullable(),
});

// TES-1402 (Ö-550, Emelie 2026-10-15) — "Den kompakta coachöversikten visar
// att en överlämning finns och vilken handling coachen behöver utföra, men
// inte onödiga medicinska eller interna detaljer." The action is the next
// step of the handoff lifecycle (sent → acknowledged → in_progress →
// resolved), the same three buttons the handoff block offers. It is derived
// from status alone, so no reason, context or requested decision is read.
// With several open, the least advanced one decides: a handoff nobody has
// confirmed yet comes first.
export const COACH_HANDOFF_ACTIONS = [
  "acknowledge",
  "start",
  "resolve",
] as const;
export const coachHandoffActionSchema = z.enum(COACH_HANDOFF_ACTIONS);
export type CoachHandoffAction = z.infer<typeof coachHandoffActionSchema>;

const HANDOFF_ACTION_BY_STATUS: Partial<
  Record<HandoffStatus, CoachHandoffAction>
> = {
  sent: "acknowledge",
  acknowledged: "start",
  in_progress: "resolve",
};

export const coachSummarySchema = z.object({
  patientId: z.string(),
  mainHelp: z.array(coachSummaryLineSchema),
  goalsAndNeeds: z.object({
    lines: z.array(coachSummaryLineSchema),
    // Rows not shown here; all of them are in "Patienten i korthet".
    more: z.number().int().nonnegative(),
  }),
  nextStep: z.object({
    kind: coachNextStepKindSchema,
    // When the waiting thing arrived (message, check-in, handoff), when the
    // follow-up is due, or when the next check-in opens. Null when unknown.
    at: z.string().nullable(),
    // How many of this kind wait (messages, check-ins, due follow-ups,
    // handoffs); 0 for the kinds that are not a count.
    count: z.number().int().nonnegative(),
    // The coach's own follow-up text, for the two follow-up kinds.
    prompt: z.string().nullable(),
    // Other waiting coach work beyond this step.
    alsoWaiting: z.number().int().nonnegative(),
  }),
  handoffs: z.object({
    // Open handoffs addressed to the coach. `action` is null when none is
    // open; optional so an admin built before it reads an older api.
    toCoach: handoffCountSchema.extend({
      action: coachHandoffActionSchema.nullable().optional(),
    }),
    // The coach's own handoffs to the doctor that are not yet resolved.
    fromCoach: handoffCountSchema,
  }),
  medicalStatus: z.string().nullable(),
  // TES-1448 (Ö-709) — true when the viewer is her active assigned coach and
  // she has at least one symptom she has not marked "Borta". False for
  // everyone else, the doctor and the admin's support view included. No
  // symptom, severity, date or count is ever sent. Optional so an admin
  // built before it reads an older api.
  registeredSymptoms: z.boolean().optional(),
});
export type CoachSummary = z.infer<typeof coachSummarySchema>;

// The handoff columns the summary reads. No reason, context or resolution:
// counts and dates are all it shows, so no handoff text is ever loaded.
export interface CoachSummaryHandoffRow {
  fromRole: string;
  toRole: string;
  status: string;
  createdAt: string;
}

// TES-1448 — Ö-709, Emelie 2026-10-19: "Aktiv tilldelad coach ser en
// diskret neutral rad ”Patienten har registrerat besvär.” i
// patientöversikten, utan detaljer." No link: the coach has no access to the
// symptom graphs (Ö-715).
export const COACH_REGISTERED_SYMPTOMS_TEXT =
  "Patienten har registrerat besvär.";

export const COACH_SUMMARY_MAIN_HELP_MAX = 3;
export const COACH_SUMMARY_GOALS_MAX = 4;

function isMainHelp(item: CoachMemoryItem): boolean {
  return (
    item.category === "goal" &&
    item.sourceType === "form_submission" &&
    item.sourceTemplateKey === "f01" &&
    F01_MAIN_HELP_LEADS.some((lead) => item.body.startsWith(`${lead}:`))
  );
}

const GOALS_AND_NEEDS = new Set<CoachMemoryItem["category"]>([
  "goal",
  "desired_support",
  "relevant_consideration",
]);

const newestFirst = (a: CoachMemoryItem, b: CoachMemoryItem) =>
  b.observedAt.localeCompare(a.observedAt);

function summarizeHandoffs(
  rows: readonly CoachSummaryHandoffRow[],
  match: (r: CoachSummaryHandoffRow) => boolean,
) {
  const open = rows
    .filter(
      (r) =>
        OPEN_HANDOFF_STATUSES.includes(r.status as HandoffStatus) && match(r),
    )
    .map((r) => r.createdAt)
    .sort()
    .reverse();
  return { open: open.length, latestAt: open[0] ?? null };
}

function toCoachAction(
  rows: readonly CoachSummaryHandoffRow[],
): CoachHandoffAction | null {
  const open = new Set(
    rows.filter((r) => r.toRole === "coach").map((r) => r.status),
  );
  for (const status of OPEN_HANDOFF_STATUSES) {
    if (open.has(status)) return HANDOFF_ACTION_BY_STATUS[status] ?? null;
  }
  return null;
}

export function composeCoachSummary(input: {
  memory: CoachMemory;
  now: CoachNow;
  handoffs: readonly CoachSummaryHandoffRow[];
  // Already decided for this viewer (CoachSummaryService): only her active
  // assigned coach can be told yes.
  registeredSymptoms?: boolean;
}): CoachSummary {
  const { memory, now } = input;
  const items = memory.areas
    .flatMap((a) => a.items)
    .filter((i) => i.lifecycle === "active" && !isUnconfirmedHypothesis(i))
    .sort(newestFirst);

  const mainHelp = items.filter(isMainHelp);
  const goals = items.filter(
    (i) =>
      GOALS_AND_NEEDS.has(i.category) &&
      !isMainHelp(i) &&
      // An agreement is shown as one under "Just nu", not as a goal.
      i.factType !== "agreement",
  );

  const toCoach = {
    ...summarizeHandoffs(input.handoffs, (r) => r.toRole === "coach"),
    action: toCoachAction(input.handoffs),
  };
  const fromCoach = summarizeHandoffs(
    input.handoffs,
    (r) => r.fromRole === "coach" && r.toRole === "doctor",
  );

  return {
    patientId: now.patientId,
    mainHelp: mainHelp
      .slice(0, COACH_SUMMARY_MAIN_HELP_MAX)
      .map(coachBriefLine),
    goalsAndNeeds: {
      lines: goals.slice(0, COACH_SUMMARY_GOALS_MAX).map(coachBriefLine),
      more: Math.max(0, goals.length - COACH_SUMMARY_GOALS_MAX),
    },
    nextStep: nextStep(now, toCoach),
    handoffs: { toCoach, fromCoach },
    medicalStatus: now.medicalStatus,
    registeredSymptoms: input.registeredSymptoms ?? false,
  };
}

// The next coach task, in the work queue's own order: messages, then weekly
// check-ins (A-01: "Arbetskön ska ha sektionerna Meddelanden,
// Veckoincheckningar …"), then a follow-up that is due, then a handoff to
// her. With nothing waiting, the next planned follow-up or check-in.
function nextStep(
  now: CoachNow,
  toCoach: { open: number; latestAt: string | null },
): CoachSummary["nextStep"] {
  if (now.coachAccess === "inactive") {
    return {
      kind: now.coachOpensWithSubscription
        ? "no_coach_before_subscription"
        : "no_coach_access",
      at: null,
      count: 0,
      prompt: null,
      alsoWaiting: 0,
    };
  }
  const due = now.followUps
    .filter((f) => f.status === "due")
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  const waiting: {
    kind: CoachNextStepKind;
    count: number;
    at: string | null;
    prompt: string | null;
  }[] = [
    {
      kind: "message",
      count: now.toDo.unreadMessages,
      at: now.latestContact.patientAt,
      prompt: null,
    },
    {
      kind: "checkin",
      count: now.toDo.openCheckins,
      // The newest check-in is the open one in the normal case; when an older
      // one is open instead, the date is not known here and stays null.
      at:
        now.latestCheckin?.state === "open"
          ? now.latestCheckin.submittedAt
          : null,
      prompt: null,
    },
    {
      kind: "follow_up_due",
      count: now.toDo.dueFollowUps,
      at: due[0]?.dueAt ?? null,
      prompt: due[0]?.prompt ?? null,
    },
    {
      kind: "handoff",
      count: toCoach.open,
      at: toCoach.latestAt,
      prompt: null,
    },
  ];
  const total = waiting.reduce((n, w) => n + w.count, 0);
  const first = waiting.find((w) => w.count > 0);
  if (first) return { ...first, alsoWaiting: total - first.count };

  const scheduled = now.followUps
    .filter((f) => f.status === "scheduled")
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt))[0];
  if (scheduled) {
    return {
      kind: "follow_up_scheduled",
      at: scheduled.dueAt,
      count: 0,
      prompt: scheduled.prompt,
      alsoWaiting: 0,
    };
  }
  if (!now.nextCheckin) {
    return {
      kind: "no_checkin_without_subscription",
      at: null,
      count: 0,
      prompt: null,
      alsoWaiting: 0,
    };
  }
  return {
    kind: now.nextCheckin.openNow ? "checkin_window_open" : "next_checkin",
    at: now.nextCheckin.opensAt,
    count: 0,
    prompt: null,
    alsoWaiting: 0,
  };
}
